// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 zouwei
// Portions derived from Excalidraw (https://github.com/excalidraw/excalidraw),
// MIT License, Copyright (c) 2020 Excalidraw — hand-drawn shape options
// (roughness/fillStyle mapping) informed by packages/element/src/shape.ts.

/**
 * Hand-drawn shape rendering via rough.js, DOM-less: `rough.generator()`
 * produces a Drawable and `toPaths()` yields `{ d, fill, stroke, strokeWidth }`
 * we serialize to SVG `<path>` strings — so the core stays string-based. Seed
 * is derived from the element id so a given shape renders identically every
 * time (stable across reloads and hosts).
 */

import rough from 'roughjs'
import type { RoughGenerator } from 'roughjs/bin/generator'
import type { ShapeElement } from './types'

let gen: RoughGenerator | null = null
function generator(): RoughGenerator {
  if (!gen) gen = rough.generator()
  return gen
}

/** Stable 31-bit seed from an element id (deterministic rough output). */
export function seedFromId(id: string): number {
  let h = 2166136261
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0) % 2147483647
}

const ROUGHNESS: Record<0 | 1 | 2, number> = { 0: 0.6, 1: 1.2, 2: 2.6 }

/** Build rough options from a shape's style. */
function optionsFor(el: ShapeElement) {
  const stroke = el.color ?? '#1a1a1a'
  const opts: Record<string, unknown> = {
    seed: seedFromId(el.id),
    roughness: ROUGHNESS[(el.sloppiness ?? 1) as 0 | 1 | 2],
    stroke,
    strokeWidth: el.strokeWidth,
    preserveVertices: true,
  }
  if (el.fill && el.fill !== 'transparent') {
    opts.fill = el.fill
    opts.fillStyle = el.fillStyle === 'crossHatch' ? 'cross-hatch' : el.fillStyle ?? 'hachure'
    opts.fillWeight = Math.max(1, el.strokeWidth * 0.5)
    opts.hachureGap = Math.max(4, el.strokeWidth * 4)
  }
  return opts
}

/** The rough drawable for a shape's geometry (its own bounding box). */
function drawable(el: ShapeElement) {
  const g = generator()
  const o = optionsFor(el)
  const { x, y, width: w, height: h } = el
  switch (el.kind) {
    case 'rect':
      return g.rectangle(x, y, w, h, o)
    case 'ellipse':
      return g.ellipse(x + w / 2, y + h / 2, w, h, o)
    case 'diamond': {
      const cx = x + w / 2
      const cy = y + h / 2
      return g.polygon(
        [
          [cx, y],
          [x + w, cy],
          [cx, y + h],
          [x, cy],
        ],
        o,
      )
    }
    case 'line':
    case 'arrow': {
      if (el.points && el.points.length > 2) {
        return g.linearPath(el.points.map(([px, py]) => [x + px, y + py] as [number, number]), o)
      }
      return g.line(x, y, x + w, y + h, o)
    }
  }
}

/**
 * Serialize a shape to SVG `<path>` strings with a hand-drawn look. `extra`
 * (e.g. a stroke-dasharray) is appended to the stroked outline path only.
 */
export function roughShapeSvg(el: ShapeElement, extra = ''): string {
  const d = drawable(el)
  const paths = generator().toPaths(d)
  return paths
    .map((p) => {
      const isFill = p.fill && p.fill !== 'none'
      const dash = isFill ? '' : extra
      return `<path d="${p.d}" stroke="${p.stroke}" stroke-width="${p.strokeWidth}" fill="${p.fill ?? 'none'}" stroke-linecap="round"${dash}/>`
    })
    .join('')
}
